import {
  computed,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { AppRoutingModule } from './app/app-routing.module';
import * as Sentry from '@sentry/angular';
import { EnvHelper } from '@tourfold/libraries/shared-ui/helper/env-helper/env-helper';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { CustomToastsComponent } from '@tourfold/plugins/bw/web/funeral/shared/custom-toasts/custom-toasts.component';
import { provideToastr } from 'ngx-toastr';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { firstValueFrom } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { clearStateOnLogout } from './app/store/clear-state-on-logout.meta-reducer';
import { MessageModule } from 'primeng/message';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { FuneralRouteReuseStrategy } from '@tourfold/plugins/bw/web/funeral/funeral-route-reuse-strategy';
import { Title, BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { Router, RouteReuseStrategy } from '@angular/router';
import { AntiCacheInterceptor } from './app/bootstrap/interceptors/anti-cache.interceptor';
import { ETagInterceptor } from './app/bootstrap/interceptors/eTag.interceptor';
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import {
  FrontendConfigurationService,
  provideFrontendConfigurationShell,
} from '@tourfold/shared-kernels/frontend-configuration';
import { buildCannotReachBackendUrl } from './app/public-pages/errors/cannot-reach-backend/cannot-reach-backend-url';
import { WEB_AUTH_FRONTEND_CONFIG_PROVIDERS } from './app/bootstrap/providers/web-auth-frontend-config.providers';
import { provideBuildInfo } from '@tourfold/shared-kernels/build-info';
import BuildInfoJson from './assets/build.info.json';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { WEB_API_CLIENT_PROVIDERS } from './app/bootstrap/providers/web-api-clients.providers';
import { provideSystemRenderers } from '@tourfold/features/custom-objects/system-renderers';

import { providePrimeNG } from 'primeng/config';
import { tourfoldWebappTheme, MAPBOX_CONFIG } from '@tourfold/libraries/shared-ui';
import {
  FRONTEND_VERSION,
  provideLazyChunkErrorDetection,
} from '@tourfold/libraries/shared-util';
import { Settings } from './settings/settings';
import {
  TOUR_SERVICE,
  TourService as BaseTourService,
  BrandsStorageService,
} from '@tourfold/libraries/core-domain';
import BrandsJson from './assets/data/brands.json';
import PackageJson from '../../../package.json';
import { PluginRegistry, WebPluginLoaderService } from '@tourfold/shared-kernels/plugin-engine/web';
import {
  DOCUMENT_MANAGEMENT_CAPABILITIES,
  type DocumentManagementCapabilities,
} from '@tourfold/features/document-management';
import { COMMENTS_CAPABILITIES, type CommentsCapabilities } from '@tourfold/features/comments';
import {
  RESOURCE_ACCESS_FALLBACK_REDIRECT,
  ResourceAccessService,
} from '@tourfold/shared-kernels/resource-access';
import {
  BROWSER_USER_TOKEN_STORE_KEYS,
  BrowserUserTokenStore,
  USER_TOKEN_STORE,
} from '@tourfold/shared-kernels/auth';
import { WEB_APP_TOKEN_STORE_KEYS } from './app/app.token-store';
import { WEB_REALTIME_PROVIDERS } from './app/bootstrap/providers/web-realtime-providers';
import { AuthService } from './app/services/auth.service';
import {
  DEFAULT_TRANSLATION_LANG,
  registerPlatformAngularLocaleData,
  DocumentLocaleAttributesService,
  SUPPORTED_TRANSLATION_LANGS,
  PlatformI18nService,
} from '@tourfold/shared-kernels/i18n';

registerPlatformAngularLocaleData();

export function initialise(
  translate: TranslateService,
  frontendConfigService: FrontendConfigurationService,
  platformI18n: PlatformI18nService,
  documentAttributes: DocumentLocaleAttributesService
): () => Promise<unknown> {
  return async () => {
    documentAttributes.startSync();
    translate.addLangs([...SUPPORTED_TRANSLATION_LANGS]);
    translate.setFallbackLang(DEFAULT_TRANSLATION_LANG);

    const lang = platformI18n.resolveTranslationLang();

    // firstValueFrom awaits the Observable returned by translate.use(), which
    // completes only after the translation files are fully fetched and merged.
    // The previous onLangChange subscription approach had a subtle race: if the
    // router navigated during bootstrap (e.g. tenant-not-found) before the
    // subscription fired, any translate.instant() call would return raw keys.
    const translatePromise = firstValueFrom(translate.use(lang)).catch(() => undefined);
    const tenantPromise = frontendConfigService.loaded;

    function printBuildInfo(): void {
      // Print build info
      // Need to so we can inspect the build version in the browser after deployment
    }

    printBuildInfo();

    return Promise.all([translatePromise, tenantPromise]);
  };
}

const tenantHostSegment: string | undefined = window.location.host.split('.')?.[0]?.split(':')?.[0];

if (environment.production) {
  enableProdMode();
}

// initialiseSentry here until we have the build info;
initialiseSentry();

function initialiseSentry(release?: string): void {
  Sentry.init({
    dsn: 'https://9df391926c6463fc1099b1f5ae078d26@o4509212613869568.ingest.de.sentry.io/4509212821618768',
    environment: EnvHelper.getWebEnv(),
    enabled: environment.production,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    initialScope: {
      tags: { tenant: tenantHostSegment },
    },
    release: release ?? '-',
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  });
}

BrandsStorageService.setDefaultBrands(BrandsJson);

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      FlexLayoutModule,
      MessageModule,
      // Empty root anchors so plugin-contributed StoreModule.forFeature(…) +
      // EffectsModule.forFeature(…) calls (BW funeral) have a parent to register
      // against. Web-app's own auth/users/roles NgRx slices were deleted in
      // favour of signal-based AuthService + CurrentUserService.
      StoreModule.forRoot({}, { metaReducers: [clearStateOnLogout] }),
      EffectsModule.forRoot([]),
      ButtonModule,

      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
      })
    ),
    {
      provide: RESOURCE_ACCESS_FALLBACK_REDIRECT,
      useValue: ['/app', 'tours-planning'] as const,
    },
    ...provideFrontendConfigurationShell({
      defaultRoute: '/app',
      outageRedirect: (loc) => buildCannotReachBackendUrl(loc),
      tenantNotFoundRedirect: '/tenant-not-found',
    }),
    ...WEB_AUTH_FRONTEND_CONFIG_PROVIDERS,
    provideToastr({
      toastComponent: CustomToastsComponent,
      toastClass: 'toastr',
      closeButton: true,
      tapToDismiss: false,
    }),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
      }),
    }),
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const frontendConfigService = inject(FrontendConfigurationService);
      const platformI18n = inject(PlatformI18nService);
      const documentAttributes = inject(DocumentLocaleAttributesService);
      const pluginLoader = inject(WebPluginLoaderService);
      const pluginRegistry = inject(PluginRegistry);

      // Kick off the frontend-configuration fetch from this async context
      // (NOT from the kernel service constructor) to avoid Zone.js microtask drain.
      frontendConfigService.bootstrap();
      // Translations + public tenant config (includes all plugins).
      await initialise(translate, frontendConfigService, platformI18n, documentAttributes)();

      // Plugins are loaded once from the bootstrap config response. The
      // backend returns the same plugin list on the public and authenticated
      // endpoints, so there is no need to re-sync after login.
      const pluginIds = frontendConfigService.webPlugins();
      if (pluginIds.length > 0) {
        const loadedPlugins = await pluginLoader.loadPlugins(pluginIds);
        if (loadedPlugins.length > 0) {
          pluginRegistry.registerAll(loadedPlugins);
          const tenantId = frontendConfigService.tenantId() ?? '';
          await pluginRegistry.activateAll(
            loadedPlugins.map(() => ({ tenantId }))
          );
        }
      }
    }),
    { provide: 'environment', useValue: environment },
    // Frontend version (TF-1459) — bundled from monorepo root package.json,
    // consumed by FrontendVersionService + the createFrontendVersionMiddleware
    // wired into the generated-client middleware stack in web-api-clients.providers.ts.
    { provide: FRONTEND_VERSION, useValue: (PackageJson as { version: string }).version },
    // Surface the version banner when a lazy-chunk load fails after a deploy
    // (TF-1984) instead of leaving the menu click silently dead.
    provideLazyChunkErrorDetection(),
    { provide: BROWSER_USER_TOKEN_STORE_KEYS, useValue: WEB_APP_TOKEN_STORE_KEYS },
    { provide: USER_TOKEN_STORE, useClass: BrowserUserTokenStore },
    provideBuildInfo({ buildId: BuildInfoJson.build_id, version: BuildInfoJson.version }),
    ...WEB_REALTIME_PROVIDERS,
    // Backend calls use fetch-based API clients with middleware; the only remaining
    // HttpClient consumer is ngx-translate, so we keep ETag + AntiCache for that.
    { provide: HTTP_INTERCEPTORS, useClass: ETagInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AntiCacheInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    Title,

    { provide: RouteReuseStrategy, useClass: FuneralRouteReuseStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: tourfoldWebappTheme,
        options: {
          darkModeSelector: '.app-theme-dark',
        },
      },
    }),
    // Mapbox config for tf-mapbox-map component
    {
      provide: MAPBOX_CONFIG,
      useFactory: () => {
        const mapSettings = Settings.getSettings().map;
        return {
          accessToken: environment.mapboxAccessToken,
          defaultStyle: mapSettings.defaultStyle,
          defaultCenter: [mapSettings.defaultLocation.lon, mapSettings.defaultLocation.lat] as [
            number,
            number,
          ],
          defaultZoom: mapSettings.defaultZoomLevel,
        };
      },
    },
    // Generic services (tenant-specific services like JOB_SERVICE are
    // contributed by plugins via getProviders() — see BW_WEB_PLUGIN)
    { provide: TOUR_SERVICE, useExisting: BaseTourService },
    ...WEB_API_CLIENT_PROVIDERS,
    // Specialized renderers for relations to system objects (Tags, Location, …). Resolved by
    // tf-field-renderer through AttributeComponentRegistry; see the lib's provider for details.
    provideSystemRenderers(),
    // Wires the document-management lib's capability gates to the auth-derived ResourceAccessService
    // so the detail pane can hide the inline "Create '<query>'" suggestion for users without the
    // `tags.create` action, lock chips read-only for users without `documents.update`, and disable
    // the folder browser's upload / "New folder" buttons for users without `documents.create`.
    {
      provide: DOCUMENT_MANAGEMENT_CAPABILITIES,
      useFactory: (access: ResourceAccessService): DocumentManagementCapabilities => ({
        canUpdateDocuments: computed(() => access.canPerform('documentManagement', 'update')),
        canDeleteDocuments: computed(() => access.canPerform('documentManagement', 'delete')),
        canCreateTags: computed(() => access.canPerform('tags', 'create')),
        canCreateDocuments: computed(() => access.canPerform('documentManagement', 'create')),
      }),
      deps: [ResourceAccessService],
    },
    // Wires the comments lib's capability gates to the auth-derived ResourceAccessService so
    // moderator-only menu items (edit any / delete any / restore / edit history) only render
    // for users with the `comments.moderate` grant. Authoring on own comments is gated client-
    // side by the row component's author-id check and only requires `comments.create`.
    {
      provide: COMMENTS_CAPABILITIES,
      useFactory: (access: ResourceAccessService, auth: AuthService): CommentsCapabilities => ({
        canCreateComments: computed(() => access.canPerform('comments', 'create')),
        // `update` and `delete` both back the same `comments.moderate` grant, so either is
        // sufficient as the moderator gate. We pick `update` because it covers edit / restore
        // (restore reuses UPDATE since FrontendResourceAccess has no dedicated RESTORE action).
        canModerateComments: computed(() => access.canPerform('comments', 'update')),
        currentUserId: computed(() => auth.currentUser()?.id ?? null),
      }),
      deps: [ResourceAccessService, AuthService],
    },
  ],
}).catch((err) => console.error(err));
